import { useDrag, useDrop } from "react-dnd"
import { WorkspaceTreeMaxDepth } from "../constants"
import { ID } from "@nova/constants"
import {
    findMaxTreeDepth,
    findTagAncestorIds,
    flattenTagTree,
    isDraggingWorkspaceEntity,
} from "../module"
import {
    WorkspaceTreeItem,
    selectLegacyWorkspaceTree,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import { selectProjectsByTagFilter } from "features/entity-caching/fires/selectors"
import { useMemo, useRef } from "react"
import { WorkspaceDragItem, WorkspaceEntityType } from "../types"

type UseWorkspaceFolderDragAndDropProps = {
    id: ID
    subFolders?: WorkspaceTreeItem[]
    isEditor: boolean
    isRenaming: boolean
    handleMove: (id: ID, type: WorkspaceEntityType, newParentFolderId?: ID) => void
    isInFavorites: boolean
}

export const useWorkspaceFolderDragAndDrop = ({
    id,
    subFolders,
    isEditor,
    isRenaming,
    handleMove,
    isInFavorites,
}: UseWorkspaceFolderDragAndDropProps) => {
    const sidebarTree = useAppSelector(selectLegacyWorkspaceTree)
    const tagChain = (sidebarTree && findTagAncestorIds(sidebarTree, id)) || []
    const projects = useAppSelector(selectProjectsByTagFilter(tagChain))

    // Immediate children only.
    const projectIdsInThisFolder = useMemo(() => {
        const set = new Set<ID>()
        projects.forEach((project) => set.add(project.id))
        return set
    }, [projects])

    // Immediate children only.
    const subFolderIdsInThisFolder = useMemo(
        () => new Set(subFolders?.map((subFolder) => subFolder.id)),
        [subFolders]
    )

    const [{ isDragging }, dragRef] = useDrag(() => {
        const flattenedChildren: Record<ID, WorkspaceTreeItem> = {}
        if (subFolders) flattenTagTree(subFolders, flattenedChildren)

        return {
            type: WorkspaceEntityType.Folder,
            item: {
                id,
                type: WorkspaceEntityType.Folder,
                depthOfChildren: findMaxTreeDepth({ subTags: subFolders || [] }),
                children: new Set(Object.keys(flattenedChildren)),
                isFromFavorites: isInFavorites,
            } satisfies WorkspaceDragItem,
            collect: (monitor) => ({
                isDragging: !!monitor.isDragging(),
            }),
            canDrag: !isRenaming && isEditor,
        }
    }, [isRenaming, isEditor, id, subFolders, isInFavorites])

    const [{ isOver, canDrop, anythingIsDragging }, dropRef] = useDrop(
        () => ({
            accept: [WorkspaceEntityType.Project, WorkspaceEntityType.Folder],
            drop: (item: WorkspaceDragItem) => handleMove(item.id, item.type, id),
            collect: (monitor) => ({
                isOver: !!monitor.isOver(),
                canDrop: !!monitor.canDrop(),
                anythingIsDragging: isDraggingWorkspaceEntity(monitor),
            }),
            canDrop: (item) => {
                if (item.isFromFavorites && !isInFavorites) return false
                if (item.type === WorkspaceEntityType.Project) {
                    return !projectIdsInThisFolder.has(item.id)
                }
                if (item.children.has(id) || id === item.id) return false
                if (subFolderIdsInThisFolder.has(item.id)) return false
                return item.depthOfChildren + tagChain.length <= WorkspaceTreeMaxDepth
            },
        }),
        [id, projectIdsInThisFolder, tagChain, subFolderIdsInThisFolder, isInFavorites]
    )

    const dragAndDropRef = useRef<HTMLDivElement>(null)
    dragRef(dragAndDropRef)
    dropRef(dragAndDropRef)

    return {
        dragAndDropRef,
        isOver,
        canDrop,
        isDragging,
        anythingIsDragging,
    }
}
